/**
 * Magic-link onboarding redemption page.
 *
 * Click-to-confirm pattern: email clients sometimes prefetch URLs (link
 * previews, security scanners, iOS Mail). To avoid those silent visits
 * burning the one-time token, we:
 *   1. GET /onboard/magic/{token}/peek  → READ-ONLY, safe for prefetch.
 *      Returns the clinic name so we can render a friendly confirmation card.
 *   2. User explicitly clicks "Confirm sign-in".
 *   3. POST /onboard/magic/{token}/redeem → consumes the token + mints JWT.
 *   4. await refreshUser() so /auth/me is hydrated before navigating.
 *   5. nav('/change-password').
 *
 * Route registered in PublicRoutes.jsx: `/onboard/magic/:token`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { CheckCircle2, Loader2, AlertTriangle, Mail, RefreshCw, Building2, KeyRound } from 'lucide-react';

export default function OnboardMagic() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  // States: peeking → confirm → redeeming → success | error
  const [state, setState] = useState('peeking');
  const [info, setInfo] = useState(null);            // { clinic_name, owner_email_masked, … }
  const [error, setError] = useState('');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const peekedRef = useRef(false);

  // Peek on mount (cheap, idempotent, prefetch-safe).
  useEffect(() => {
    if (peekedRef.current) return;
    peekedRef.current = true;
    (async () => {
      try {
        const { data } = await api.get(`/onboard/magic/${token}/peek`);
        setInfo(data);
        setState('confirm');
      } catch (e) {
        setError(e?.response?.data?.detail || 'This link is no longer valid.');
        setState('error');
      }
    })();
  }, [token]);

  const confirm = async () => {
    setState('redeeming');
    try {
      await api.post(`/onboard/magic/${token}/redeem`);
      if (refreshUser) await refreshUser();
      setState('success');
      setTimeout(() => nav('/change-password', { replace: true }), 1000);
    } catch (e) {
      setError(e?.response?.data?.detail || 'This link is no longer valid.');
      setState('error');
    }
  };

  const resend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendBusy(true);
    try { await api.post('/onboard/magic/resend', { email: resendEmail.trim() }); }
    catch (_) { /* anti-enumeration; always treat as success */ }
    setResendDone(true);
    setResendBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF6EE] flex items-center justify-center p-6">
      <div className="surface border border-soft rounded-2xl max-w-md w-full p-8" data-testid="onboard-magic-card">

        {state === 'peeking' && (
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-brand mx-auto animate-spin" data-testid="om-peeking"/>
            <h1 className="text-xl font-heading font-bold mt-4">Loading your link…</h1>
          </div>
        )}

        {state === 'confirm' && info && (
          <div data-testid="om-confirm">
            <div className="flex items-center gap-3 pb-4 border-b border-soft">
              <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-brand"/>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-accent font-semibold">FINISH SIGNING IN</div>
                <h1 className="text-xl font-heading font-bold truncate">{info.clinic_name}</h1>
              </div>
            </div>
            <p className="text-sm text-fg-muted mt-4">
              You're about to sign in to <strong>{info.clinic_name}</strong> as the admin owner.
              {info.owner_email_masked && <> The link was sent to <span className="text-fg font-medium">{info.owner_email_masked}</span>.</>}
            </p>
            <p className="text-xs text-fg-muted mt-3">
              We require this click step to protect against automatic link scanners.
            </p>
            <button onClick={confirm}
                    className="btn-primary w-full mt-5 inline-flex items-center justify-center gap-2 text-base py-3"
                    data-testid="om-confirm-btn">
              <KeyRound className="w-4 h-4"/> Confirm sign-in
            </button>
            <p className="text-[11px] text-fg-muted text-center mt-3">
              This link only works once. You'll be asked to set a password right after.
            </p>
          </div>
        )}

        {state === 'redeeming' && (
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-brand mx-auto animate-spin" data-testid="om-loading"/>
            <h1 className="text-xl font-heading font-bold mt-4">Signing you in…</h1>
            <p className="text-sm text-fg-muted mt-2">One moment.</p>
          </div>
        )}

        {state === 'success' && (
          <div className="text-center" data-testid="om-success">
            <CheckCircle2 className="w-12 h-12 text-[#1B5E20] mx-auto"/>
            <h1 className="text-xl font-heading font-bold mt-4">You're in!</h1>
            <p className="text-sm text-fg-muted mt-2">Redirecting you to set your password…</p>
          </div>
        )}

        {state === 'error' && (
          <div className="text-center" data-testid="om-error">
            <AlertTriangle className="w-10 h-10 text-[#B83A3A] mx-auto"/>
            <h1 className="text-xl font-heading font-bold mt-4">Link expired or already used</h1>
            <p className="text-sm text-fg-muted mt-2">{error}</p>
            {resendDone ? (
              <div className="mt-5 p-4 bg-[#E8F5E9]/50 rounded-lg text-sm" data-testid="om-resend-done">
                <Mail className="w-5 h-5 text-[#1B5E20] mx-auto"/>
                <p className="mt-2">If <strong>{resendEmail}</strong> is on file with us, a fresh link is on its way.</p>
              </div>
            ) : (
              <form onSubmit={resend} className="mt-5 space-y-3 text-left" data-testid="om-resend-form">
                <p className="text-xs text-fg-muted text-center">Enter your email and we'll send a new link.</p>
                <input className="input w-full" type="email" required value={resendEmail}
                       onChange={(e) => setResendEmail(e.target.value)}
                       placeholder="you@yourclinic.com" data-testid="om-resend-email"/>
                <button type="submit" disabled={resendBusy}
                        className="btn-primary w-full inline-flex items-center justify-center gap-2"
                        data-testid="om-resend-submit">
                  {resendBusy ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
                  Send me a fresh link
                </button>
                <a href="/sign-in" className="block text-xs text-fg-muted hover:text-brand mt-3 text-center">Already have a password? Sign in →</a>
              </form>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
